import React, { useState, useEffect, useCallback } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Plus, Trash2, CheckCircle2, FileMinus, Download, Upload } from 'lucide-react';
import { fmt } from '../../lib/accountingStore';
import { apiBills, apiVendors } from '../../lib/api';
import { toCSV, downloadFile, parseCSV, readFileAsText } from '../../lib/importExport';
import { toast } from 'sonner';
import { shiftDays, todayISO } from '../../lib/dates';

const sBadge = (s) => ({ Paid: 'bg-emerald-100 text-emerald-700', Open: 'bg-blue-100 text-blue-700', Overdue: 'bg-red-100 text-red-700' }[s] || 'bg-slate-100 text-slate-700');

export default function Bills() {
  const [bills, setBills] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [open, setOpen] = useState(false);
  const load = useCallback(() => {
    apiBills.list().then((d) => setBills(d || [])).catch(() => setBills([]));
    apiVendors.list().then((d) => setVendors(d || [])).catch(() => setVendors([]));
  }, []);
  useEffect(() => { load(); }, [load]);
  const onDelete = async (id) => { try { await apiBills.remove(id); load(); toast.success('Bill deleted'); } catch { toast.error('Failed'); } };
  const onPay = async (b) => { try { await apiBills.update(b.id, { ...b, status: 'Paid' }); load(); toast.success('Bill paid'); } catch { toast.error('Failed'); } };
  const totalOpen = bills.filter((b) => b.status !== 'Paid').reduce((a, b) => a + (Number(b.amount) || 0), 0);
  return (
    <div className="p-6 lg:p-8 space-y-5" data-testid="acct-bills">
      <div className="flex justify-between items-center flex-wrap gap-3">
        <div><h2 className="text-2xl font-extrabold tracking-tight text-slate-900">Bills</h2><p className="text-slate-500 text-sm">{fmt(totalOpen)} open A/P across {bills.filter((b) => b.status !== 'Paid').length} bills</p></div>
        <Button onClick={() => setOpen(true)} className="bg-[#8BC53F] hover:bg-[#6FA82C] gap-1.5" data-testid="new-bill-btn"><Plus className="w-4 h-4" />New Bill</Button>
      </div>
      <div className="flex gap-2 -mt-2">
        <label className="inline-flex items-center gap-1.5 px-3 h-9 bg-white border border-slate-200 rounded-md text-xs font-semibold text-slate-700 hover:bg-slate-50 cursor-pointer"><Upload className="w-3.5 h-3.5" />Import CSV
          <input type="file" accept=".csv" className="hidden" onChange={async (e) => {
            const f = e.target.files?.[0]; if (!f) return;
            const parsed = parseCSV(await readFileAsText(f));
            const docs = parsed.map((r, i) => ({ id: r.ID || `BILL-${Date.now()}-${i}`, vendorId: r['Vendor ID'] || '', vendorName: r.Vendor || '', date: r.Date || todayISO(), due: r.Due || todayISO(), amount: Number(r.Amount) || 0, status: r.Status || 'Open', memo: r.Memo || '' }));
            try { await apiBills.bulk(docs); load(); toast.success(`Imported ${docs.length} bills`); } catch { toast.error('Import failed'); }
            e.target.value = '';
          }} /></label>
        <Button size="sm" variant="outline" onClick={() => { downloadFile('xpertpests-bills.csv', toCSV(bills, [{ key: 'id', header: 'ID' }, { key: 'vendorName', header: 'Vendor' }, { key: 'date', header: 'Date' }, { key: 'due', header: 'Due' }, { key: 'amount', header: 'Amount' }, { key: 'status', header: 'Status' }, { key: 'memo', header: 'Memo' }])); toast.success('Exported bills.csv'); }} className="gap-1.5 h-9"><Download className="w-3.5 h-3.5" />Export CSV</Button>
      </div>
      <Card className="border-slate-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs text-slate-500 font-semibold uppercase tracking-wider">
            <tr><th className="px-5 py-3 text-left">Bill</th><th className="px-5 py-3 text-left">Vendor</th><th className="px-5 py-3 text-left">Date</th><th className="px-5 py-3 text-left">Due</th><th className="px-5 py-3 text-left">Status</th><th className="px-5 py-3 text-right">Amount</th><th className="px-5 py-3"></th></tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {bills.map((b) => (
              <tr key={b.id} className="hover:bg-slate-50">
                <td className="px-5 py-4 font-semibold text-[#6FA82C]">{b.id}</td>
                <td className="px-5 py-4"><div className="font-medium text-slate-900">{b.vendorName || vendors.find((v) => v.id === b.vendorId)?.name || '—'}</div><div className="text-xs text-slate-500">{b.memo}</div></td>
                <td className="px-5 py-4 text-slate-600">{b.date}</td>
                <td className="px-5 py-4 text-slate-600">{b.due}</td>
                <td className="px-5 py-4"><Badge className={`${sBadge(b.status)} border-0`}>{b.status}</Badge></td>
                <td className="px-5 py-4 text-right font-bold tabular-nums">{fmt(b.amount)}</td>
                <td className="px-5 py-4 text-right">
                  <div className="flex gap-1 justify-end">
                    {b.status !== 'Paid' && <Button size="sm" variant="ghost" onClick={() => onPay(b)}><CheckCircle2 className="w-4 h-4 text-emerald-600" /></Button>}
                    <Button size="sm" variant="ghost" onClick={() => onDelete(b.id)}><Trash2 className="w-4 h-4 text-red-600" /></Button>
                  </div>
                </td>
              </tr>
            ))}
            {bills.length === 0 && <tr><td colSpan={7} className="text-center py-10 text-slate-500"><FileMinus className="w-8 h-8 text-slate-300 mx-auto mb-2" />No bills yet</td></tr>}
          </tbody>
        </table>
      </Card>
      <BillDialog open={open} setOpen={setOpen} vendors={vendors} onSave={load} />
    </div>
  );
}

function BillDialog({ open, setOpen, vendors, onSave }) {
  const [vendorId, setVendorId] = useState('');
  const [date, setDate] = useState(todayISO());
  const [due, setDue] = useState(shiftDays(todayISO(), 30));
  const [amount, setAmount] = useState(''); const [memo, setMemo] = useState('');
  const submit = async () => {
    if (!amount) { toast.error('Amount required'); return; }
    const vendorName = vendors.find((v) => v.id === vendorId)?.name || '';
    try { await apiBills.create({ id: `BILL-${Date.now().toString(36)}`, vendorId, vendorName, date, due, amount: Number(amount), status: 'Open', memo }); onSave(); setOpen(false); setAmount(''); setMemo(''); toast.success('Bill added'); } catch { toast.error('Failed'); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent data-testid="bill-dialog">
        <DialogHeader><DialogTitle>New Bill</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div><Label>Vendor</Label><Select value={vendorId} onValueChange={setVendorId}><SelectTrigger className="mt-1.5"><SelectValue placeholder="Optional…" /></SelectTrigger><SelectContent>{vendors.map((v) => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}</SelectContent></Select></div>
          <div className="grid grid-cols-2 gap-3"><div><Label>Date</Label><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="mt-1.5" /></div><div><Label>Due Date</Label><Input type="date" value={due} onChange={(e) => setDue(e.target.value)} className="mt-1.5" /></div></div>
          <div><Label>Amount</Label><Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} className="mt-1.5" data-testid="bill-amount-input" /></div>
          <div><Label>Memo</Label><Input value={memo} onChange={(e) => setMemo(e.target.value)} className="mt-1.5" /></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={submit} className="bg-[#8BC53F] hover:bg-[#6FA82C]" data-testid="bill-save-btn">Save Bill</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
